import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { FallbackScreen } from '../components/FallbackScreen';
import { logAnalytics } from '../utils/database';
import { AlertCircle, Headphones } from 'lucide-react';
import { DynamicProForm } from '../components/forms/DynamicProForm';

interface ProFormConfig {
  id: string;
  route_path: string;
  form_name: string;
  form_description: string;
  input_schema: any[];
  request_type: string;
  app_id: string;
  credit_cost: number;
  example_media_url?: string;
  example_media_type?: 'image' | 'video' | 'audio';
  hy_user_id: string;
  created_at: string;
  updated_at: string;
}

export const PublicProPage = () => {
  const { routePath } = useParams<{ routePath: string }>();
  const { setSession: setGlobalSession, session: storeSession } = useAppStore();

  const [session, setSession] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);

  const [proFormConfig, setProFormConfig] = useState<ProFormConfig | null>(null);
  const [formConfigLoading, setFormConfigLoading] = useState(true);
  const [formConfigError, setFormConfigError] = useState<string | null>(null);

  // 1. Fetch Pro Form Configuration
  useEffect(() => {
    const fetchProFormConfig = async () => {
      if (!routePath) {
        setFormConfigError('Route path is missing.');
        setFormConfigLoading(false);
        return;
      }

      try {
        setFormConfigLoading(true);
        setFormConfigError(null);

        const { data, error } = await supabase
          .from('pro_forms')
          .select('*')
          .eq('route_path', `/${routePath}`).maybeSingle();

        if (error) {
          setFormConfigError(error.message);
          setProFormConfig(null);
        } else if (!data) {
          setFormConfigError('Pro form not found. This public page does not exist.');
          setProFormConfig(null);
        } else if (!data.app_id?.trim()) {
          setFormConfigError('This pro form has no App ID — set it when editing the form in admin.');
          setProFormConfig(null);
        } else {
          setProFormConfig(data);
          console.log('📄 [PUBLIC-PRO-PAGE] Pro form config loaded:', data);
        }
      } catch (err) {
        setFormConfigError(err instanceof Error ? err.message : 'Failed to load pro form configuration.');
        setProFormConfig(null);
      } finally {
        setFormConfigLoading(false);
      }
    };

    fetchProFormConfig();
  }, [routePath]);

  // 2. Sync Supabase session from global store (route is behind RequireAuth)
  useEffect(() => {
    if (formConfigLoading || !proFormConfig) {
      return;
    }

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        await logAnalytics('public_pro_page_access', {
          route_path: routePath,
          app_id: proFormConfig.app_id,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });

        if (!storeSession) {
          await logAnalytics('public_pro_page_session_missing', {
            route_path: routePath,
            app_id: proFormConfig.app_id,
            error: 'No Supabase session',
          });
          setAuthError('Please sign in to use this page.');
          setIsAuthenticated(false);
          return;
        }

        setSession(storeSession);
        setIsAuthenticated(true);
        setGlobalSession(storeSession);

        await logAnalytics('public_pro_page_session_established', {
          route_path: routePath,
          app_id: proFormConfig.app_id,
          user_id: storeSession.hy_user_id,
          plan: storeSession.hy_plan_dev_name
        });
      } catch (error) {
        console.error('Public pro page auth error:', error);
        await logAnalytics('public_pro_page_auth_error', {
          route_path: routePath,
          app_id: proFormConfig?.app_id,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        setAuthError('Failed to authenticate - Please try again');
      } finally {
        setIsLoading(false);
        setAuthCompleted(true);
      }
    };

    void initializeAuth();
  }, [formConfigLoading, proFormConfig, routePath, setGlobalSession, storeSession]);

  // Handle form completion
  const handleFormComplete = () => {
    // Optionally show a success message or redirect
    console.log('🎉 [PUBLIC-PRO-PAGE] Pro form submission completed!');
  };

  // Show loading state during initial authentication and config fetch
  if (formConfigLoading || isLoading || !authCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <img
            src="https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/c599a7b3-23d3-4f3a-a44b-b1801b7cdf24.gif"
            alt="Loading..."
            className="w-[500px] h-[500px] mx-auto rounded-lg"
          />
        </div>
      </div>
    );
  }

  // Show fallback if authentication failed
  if (authError || !isAuthenticated || !session) {
    return <FallbackScreen />;
  }

  // Render error states for form config
  if (formConfigError || !proFormConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-elevated border border-subtle rounded-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-status-error mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-th-primary mb-2">
            Pro Form Not Found
          </h1>
          <p className="text-th-secondary mb-6">
            {formConfigError || 'Could not load the pro form configuration for this page.'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="button-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-base flex flex-col">
      {/* Mobile Layout - Full Screen Stepper */}
      <div className="lg:hidden flex-1 flex flex-col h-full">
        <DynamicProForm
          proFormConfig={proFormConfig}
          creditCost={proFormConfig.credit_cost}
          pageTitle={proFormConfig.form_name}
          pageDescription={proFormConfig.form_description}
          exampleMediaUrl={proFormConfig.example_media_url}
          exampleMediaType={proFormConfig.example_media_type}
          onCancel={() => window.history.back()}
          onComplete={handleFormComplete}
        />
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block text-center pt-6 lg:pt-8 pb-4 px-4 flex-shrink-0 relative overflow-hidden">
        <div className="absolute inset-0 bg-elevated"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-primary/3 to-transparent opacity-50 animate-pulse"></div>
        <div className="relative z-10">
          <h1 className="text-2xl lg:text-3xl font-bold text-th-primary mb-2">
            {proFormConfig.form_name}
          </h1>
          {proFormConfig.form_description && (
            <p className="text-th-secondary text-sm lg:text-base max-w-2xl mx-auto">
              {proFormConfig.form_description}
            </p>
          )}
        </div>
      </div>

      {/* Desktop Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 h-full">
        {/* Form Section - Desktop: Left side, Mobile: Full width */}
        <div className={`${proFormConfig.example_media_url ? 'lg:w-1/2 xl:w-2/5 lg:border-r lg:border-subtle' : 'lg:w-full'} flex flex-col min-h-0 h-full`}>
          <DynamicProForm
            proFormConfig={proFormConfig}
            creditCost={proFormConfig.credit_cost}
            pageTitle={proFormConfig.form_name}
            pageDescription={proFormConfig.form_description}
            exampleMediaUrl={proFormConfig.example_media_url}
            exampleMediaType={proFormConfig.example_media_type}
            onCancel={() => window.history.back()}
            onComplete={handleFormComplete}
          />
        </div>

        {/* Example Media Section - Desktop only, right side */}
        {proFormConfig.example_media_url && (
          <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col min-h-0">
            <div className="flex-1 flex items-center justify-center bg-black">
              {proFormConfig.example_media_type === 'image' && (
                <img
                  src={proFormConfig.example_media_url}
                  alt="Example output"
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<div class="text-center text-th-secondary"><svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg><p>Example image unavailable</p></div>';
                    }
                  }}
                />
              )}
              
              {proFormConfig.example_media_type === 'video' && (
                <video
                  src={proFormConfig.example_media_url}
                  className="max-w-full max-h-full object-contain"
                  controls
                  muted
                  onError={(e) => {
                    const target = e.target as HTMLVideoElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<div class="text-center text-th-secondary"><svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg><p>Example video unavailable</p></div>';
                    }
                  }}
                />
              )}
              
              {proFormConfig.example_media_type === 'audio' && (
                <div className="w-full max-w-md space-y-6">
                  <div className="w-32 h-32 bg-status-warning/30 rounded-full flex items-center justify-center mx-auto">
                    <Headphones className="w-16 h-16 text-status-warning" />
                  </div>
                  <audio
                    src={proFormConfig.example_media_url}
                    className="w-full bg-elevated rounded-lg p-3"
                    controls
                    preload="metadata"
                    onError={(e) => {
                      const target = e.target as HTMLAudioElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="text-center text-th-secondary">Example audio unavailable</div>';
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-3/4 left-1/2 w-80 h-80 bg-status-warning/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};